import React, { FC, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PageTitle } from "../../../_metronic/layout/core";
import { fetchData, fetchFormData, toasterrormsg, toastsuccessmsg } from "../../../reusable";
import { ToastContainer } from "react-toastify";
import { AddCarRentalHeader } from "./AddCarRentalHeader";
import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";
import imageCompression from "browser-image-compression";
import { Tr } from "../../../_metronic/i18n/Tr";

type LocalImage = {
  id: string;
  file: File;
  preview: string; // object URL
};

const AddCarRentalPhotos: FC = () => {
  const t = Tr();
  const navigate = useNavigate();
  const categoryId = sessionStorage.getItem("categoryId");

  useEffect(() => {
    if (categoryId != "7") {
      navigate("../dashboard/");
    }
  }, [categoryId, navigate]);

  const location = useLocation();
  const editCarrentalId = (location.state as any)?.carrentalId || "";

  let vendorId = "";
  const vendorData = sessionStorage.getItem("vendor");
  if (vendorData) {
    const vendor = JSON.parse(vendorData);
    vendorId = vendor.vendorId;
  } else {
    console.log("No vendor data found");
  }

  const inputfileRef = useRef<HTMLInputElement | null>(null);
  const [loading, setLoading] = useState(false);
  const [disable, setDisable] = useState(false);

  // local images (files not yet uploaded). Each item has stable id + preview URL
  const [inputImage, setInputImage] = useState<LocalImage[]>([]);

  // images already uploaded on server (from API)
  const [uploadedImage, setUploadedImage] = useState<any[]>([]);

  const [carrentalDetails, setTourPackageDetails] = useState<any[]>([]);

  // set error state
  const [error, setError] = useState([
    {
      inputImage: "",
    },
  ]);

  // ---------- small helpers ----------
  const handleTextClick = () => {
    // use ref instead of DOM queries
    inputfileRef.current?.click();
  };

  const removeError = (err: any) => {
    const tmp = [...error] as any;
    tmp[0][err] = "";
    setError(tmp);
  };

  // ---------- image compression helpers ----------
  const processImage = async (imageFile: File) => {
    if (imageFile.size >= 2 * 1024 * 1024) {
      return await compressImage(imageFile);
    } else {
      return imageFile;
    }
  };

  const compressImage = async (imageFile: File) => {
    const options = {
      maxSizeMB: 1,
      maxWidthOrHeight: 1024,
      useWebWorker: true,
    };
    try {
      const compressedBlob = await imageCompression(imageFile, options);
      const extension = imageFile.name.split(".").pop();
      const newFileName = `${Date.now()}.${extension}`;
      const compressedFile = new File([compressedBlob], newFileName, {
        type: compressedBlob.type,
        lastModified: Date.now(),
      });
      return compressedFile;
    } catch (err) {
      console.error("Error compressing image:", err);
      return imageFile;
    }
  };

  // ---------- handle selecting files ----------
  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    removeError("inputImage");
    const selectedFiles = e.target.files ? Array.from(e.target.files) : [];
    if (selectedFiles.length === 0) return;

    // filter by size (hard limit 5MB per your original code)
    const validFiles = selectedFiles.filter((file) => {
      if (file.size > 5 * 1024 * 1024) {
        toasterrormsg(`${file.name} is larger than 5 MB and was not added.`);
        return false;
      }
      return true;
    });

    const MAX_IMAGES = 15;
    const remainingCapacity = MAX_IMAGES - inputImage.length - uploadedImage.length;
    if (remainingCapacity <= 0) {
      toasterrormsg(t(`Maximum ${MAX_IMAGES} images can be uploaded.`));
      // clear input value so user can re-select same files if wanted
      if (inputfileRef.current) inputfileRef.current.value = "";
      return;
    }

    // limit files to remaining capacity
    const filesToProcess = validFiles.slice(0, remainingCapacity);
    if (validFiles.length > filesToProcess.length) {
      toasterrormsg(t(`Maximum ${MAX_IMAGES} images can be uploaded.`));
    }

    // compress/process
    const processedFiles = await Promise.all(filesToProcess.map((f) => processImage(f)));

    // convert to LocalImage format with stable id and preview URL
    const newLocalImages: LocalImage[] = processedFiles.map((file) => {
      const id = (crypto && (crypto as any).randomUUID) ? (crypto as any).randomUUID() : `${Date.now()}-${Math.random()}`;
      const preview = URL.createObjectURL(file);
      return { id, file, preview };
    });

    if(uploadedImage.length > 0)
    setInputImage((prev) => [...prev, ...newLocalImages]);

    // clear file input so same files can be selected multiple times if needed
    if (inputfileRef.current) inputfileRef.current.value = "";
  };

  // ---------- drag end ----------
  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const items = Array.from(inputImage);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setInputImage(items);
  };

  // ---------- remove local image by index or id ----------
  const handleRemoveLocalImage = (index: number) => {
    setInputImage((prev) => {
      const item = prev[index];
      if (item && item.preview) {
        URL.revokeObjectURL(item.preview);
      }
      const newArr = prev.filter((_, i) => i !== index);
      return newArr;
    });
  };

  // ---------- remove uploaded image (server) ----------
  const handleRemoveImage = async (vehiclemediaId: any) => {
    // this is your original API call; keep same behaviour
    const param = {
      vehiclemediaId,
    };
    const res = await fetchData("public/deletevehiclemedia", param);
    if (res.status == "200") {
      carRentalDetailsApiCall();
    } else {
      toasterrormsg(res.message);
    }
  };

  // ---------- validation ----------
  const validate = () => {
    let isValid = true;
    const errorTmp = [...error];

    // total images that will be uploaded = existing uploadedImage + new inputImage
    const totalImages = uploadedImage.length + inputImage.length;

    if (totalImages === 0) {
      errorTmp[0].inputImage = t("Images Is Required");
      isValid = false;
    } else if (totalImages < 3) {
      // your original message said "Minimum 3 Images Is Required"
      errorTmp[0].inputImage = t("Minimum 3 Images Is Required");
      isValid = false;
    } else {
      errorTmp[0].inputImage = "";
    }

    setError(errorTmp);
    return isValid;
  };

  // ---------- submit images to server ----------
  const addCarRentalImageApi = async () => {
    if (!validate()) return;

    setDisable(true);
    setLoading(true);

    try {
      const param = new FormData();
      param.append("carrentalId", editCarrentalId);
      if (inputImage.length !== 0) {
        inputImage.forEach((fileObj) => {
          param.append("image", fileObj.file);
        });
      }

      const res = await fetchFormData("public/addCarRentalImage", param);
      if (res.status == "200") {
        toastsuccessmsg(t("Images Added Successfully!"));
        // refresh server-side images & proceed
        await carRentalDetailsApiCall();
        // navigate after small delay (preserving original behaviour)
        const state = { carrentalId: editCarrentalId };
        setTimeout(() => {
          navigate("../addcarrentalinsurance/", { state });
          setDisable(false);
        }, 1500);
      } else {
        toasterrormsg(res.message);
        setDisable(false);
      }
    } catch (err) {
      console.error(err);
      toasterrormsg("Something went wrong");
      setDisable(false);
    } finally {
      setLoading(false);
      // clear local images after successful upload (or keep depending on desired UX)
      // setInputImage([]);
    }
  };

  // ---------- get details from server ----------
  const carRentalDetailsApiCall = async () => {
    const param: any = {
      carrentalId: String(editCarrentalId),
    };
    const response: any = await fetchData("public/carrentalDetail", param);

    if (response.status == "200") {
      setTourPackageDetails(response.data);
      const data = response.data[0];
      setUploadedImage(data.images || []);
    } else {
      toasterrormsg(response.message);
    }
  };

  useEffect(() => {
    if (editCarrentalId !== "") {
      carRentalDetailsApiCall();
    }
    // cleanup on unmount: revoke object URLs
    return () => {
      inputImage.forEach((it) => {
        if (it.preview) URL.revokeObjectURL(it.preview);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once on mount

  // Also do effect to revoke URLs of removed images (defensive)
  useEffect(() => {
    // nothing needed here; handleRemoveLocalImage revokes when removing
  }, [inputImage]);

  // Debug (optional)
  useEffect(() => {
    console.log("Local inputImage: ", inputImage);
  }, [inputImage]);

  return (
    <>
      <ToastContainer />
      <PageTitle breadcrumbs={[]}>
        {editCarrentalId !== "" ? t("Edit Car Rental Photos") : t("Add Car Rental Photos")}
      </PageTitle>
      <AddCarRentalHeader carrentalId={editCarrentalId} />
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div className="form d-flex flex-column flex-lg-row">
            <div className="d-flex flex-column flex-row-fluid gap-7 gap-lg-10">
              <div className="tab-content">
                <div className="tab-pane fade show active" id="kt_ecommerce_add_product_general" role="tab-panel">
                  <div className="d-flex flex-column">
                    <div className="card card-flush py-4">
                      <div className="card-header d-flex">
                        <div className="card-title">
                          <h2>{t("Car Rental Photos")}</h2>
                        </div>

                        <div className="d-flex align-items-center w-200px w-sm-300px flex-column">
                          {carrentalDetails.length !== 0 ? (
                            <>
                              {carrentalDetails.map((completedSteps: any, index: any) => {
                                let percentage = 0;
                                if (completedSteps["completedSteps"]["basicInfo"]) percentage = 10;
                                if (completedSteps["completedSteps"]["location"]) percentage = 20;
                                if (completedSteps["completedSteps"]["carRentalInfo"]) percentage = 40;
                                if (completedSteps["completedSteps"]["photos"]) percentage = 50;
                                if (completedSteps["completedSteps"]["insurance"]) percentage = 60;
                                if (completedSteps["completedSteps"]["documentsRequired"]) percentage = 70;
                                if (completedSteps["completedSteps"]["availability"]) percentage = 70;
                                if (completedSteps["completedSteps"]["pickupDropoffInfo"]) percentage = 80;
                                if (completedSteps["completedSteps"]["policies"]) percentage = 90;
                                if (completedSteps["completedSteps"]["financeAndLegal"]) percentage = 100;
                                return (
                                  <React.Fragment key={index}>
                                    <div className="d-flex justify-content-between w-100 mt-auto">
                                      <span className="fw-bold fs-6">{t("Completion")}</span>
                                      <span className="fw-bolder fs-6">{percentage}%</span>
                                    </div>
                                    <div className="h-5px mx-3 w-100 bg-light mb-3">
                                      <div className="bg-success rounded h-5px" role="progressbar" style={{ width: `${percentage}%` }}></div>
                                    </div>
                                  </React.Fragment>
                                );
                              })}
                            </>
                          ) : (
                            <>
                              <div className="d-flex justify-content-between w-100 mt-auto">
                                <span className="fw-bold fs-6">{t("Completion")}</span>
                                <span className="fw-bolder fs-6">0%</span>
                              </div>
                              <div className="h-5px mx-3 w-100 bg-light mb-3">
                                <div className="bg-success rounded h-5px" role="progressbar" style={{ width: "0%" }}></div>
                              </div>
                            </>
                          )}
                        </div>
                      </div>

                      <div className="card-body pt-0">
                        <label className="fs-4 form-label">{t("Upload images")}</label>
                        <br />
                        <input
                          ref={inputfileRef}
                          style={{ display: "none" }}
                          className="modaldocument"
                          type="file"
                          onChange={handleImageChange}
                          accept="image/*"
                          multiple
                        />
                        <span className="btn btn-light text-primary cursor-pointer " onClick={handleTextClick}>
                          {t("Attach Images")}
                        </span>
                        <div className="text-danger fw-bold">{error[0].inputImage}</div>

                        {/* uploaded images from server */}
                        <div className="row mt-3">
                          {uploadedImage && uploadedImage.length > 0
                            ? uploadedImage.map((imageList, index) => (
                                <div className="col-lg-2 col-4 d-flex flex-column mb-5" key={index}>
                                  <button
                                    className="btn btn-danger p-1 px-2 rounded-circle"
                                    style={{
                                      width: "20px",
                                      height: "20px",
                                      fontSize: "12px",
                                      alignSelf: "end",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      position: "relative",
                                      top: "7%",
                                      right: "-3%",
                                    }}
                                    onClick={() => handleRemoveImage(imageList["vehiclemediaId"])}
                                  >
                                    X
                                  </button>
                                  <div style={{ maxWidth: "fit-content" }} className="p-2 shadow rounded-1">
                                    <img src={imageList["mediaPath"]} width="100%" className="rounded-1" alt={`uploaded-${index}`} />
                                  </div>
                                </div>
                              ))
                            : null}
                        </div>

                        {/* local images with drag & drop */}
                        <DragDropContext onDragEnd={handleDragEnd}>
                          <Droppable droppableId="image-list" direction="horizontal">
                            {(provided) => (
                              <div className="row mt-3" ref={provided.innerRef} {...provided.droppableProps}>
                                {inputImage.map((item, index) => (
                                  <Draggable key={item.id} draggableId={item.id} index={index}>
                                    {(providedDraggable) => (
                                      <div
                                        className="col-lg-2 col-4 d-flex flex-column mb-4"
                                        ref={providedDraggable.innerRef}
                                        {...providedDraggable.draggableProps}
                                        {...providedDraggable.dragHandleProps}
                                      >
                                        <button
                                          className="btn btn-danger p-1 px-2 rounded-circle"
                                          style={{
                                            width: "20px",
                                            height: "20px",
                                            fontSize: "12px",
                                            alignSelf: "end",
                                            position: "relative",
                                            top: "7%",
                                            right: "-3%",
                                          }}
                                          onClick={() => handleRemoveLocalImage(index)}
                                        >
                                          X
                                        </button>

                                        <div className="p-2 shadow rounded-1" style={{ maxWidth: "fit-content" }}>
                                          <img src={item.preview} width="100%" className="rounded-1" alt={`local-${index}`} />
                                        </div>
                                      </div>
                                    )}
                                  </Draggable>
                                ))}

                                {provided.placeholder}
                              </div>
                            )}
                          </Droppable>
                        </DragDropContext>
                      </div>
                    </div>

                    <div className="d-flex justify-content-end">
                      <button type="button" disabled={disable} className="btn btn-primary" onClick={addCarRentalImageApi}>
                        <span className="indicator-label">{t("Next")}</span>
                        {loading && (
                          <span className="spinner-border spinner-border-sm mx-2" role="status" aria-hidden="true"></span>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>      
    </>
  );
};

export { AddCarRentalPhotos };
